<script setup>
import { ref, computed, onMounted, watch, inject } from 'vue'
import dayjs from 'dayjs'
import { useRouter } from 'vue-router'
import learningApiUser from '@src/services/api/learning/user'
import { commonUtil } from '@utils'
import { downloadTraineePdf, isSessionExpiredPdfError } from '@utils/pdfDownload'
import Modal from '@components/modal/Modal.vue'

const router = useRouter()
const rootAppProvide = inject('rootAppProvide')

const props = defineProps({
  courseId: {
    type: String,
    required: true
  }
})
const detail = ref(null)
const keywords = ref([])
const questionnaire = ref([])

const traineeStatus = ref({
  paymentSelect: null, // 支払い方法選択済か。選択済ならSEVENTEEN_ELEVENなど
  paymentStatus: null, // 支払い状況。-1:未払い, 1:支払済, -2:返金済
  paymentKonbiniCode: '' // コンビニ支払いの場合のコンビニコード
})

// モーダル設定
const showConfirmPDFModal = ref(false)

// 申込講座詳細取得
const getDetail = () => {
  rootAppProvide.value.openContentsLoading()
  console.log('props.courseId', props.courseId)
  const getDetailParams = {
    params: {
      with: 'course.location'
    },
    custom: {
      params: { traineeId: props.courseId }
    }
  }
  const getTraineeDetailParams = {
    params: {
      with: 'payments_en'
    },
    custom: {
      params: { traineeId: props.courseId }
    }
  }

  // 講座詳細と講座支払い詳細を取得
  Promise.all([
    learningApiUser.getTraineeDetail(getDetailParams),
    learningApiUser.getTraineeDetail(getTraineeDetailParams)
  ]).then((res) => {
    console.log('res', res)
    if (!res[0].data.course) {
      return false
    }
    detail.value = res[0].data
    if (res[0].data.course.questionnaire?.length > 0) {
      questionnaire.value = res[0].data.course.questionnaire.map(() => '')
    }
    console.log('getTraineeDetail支払い状況取得 res', res[1].data)
    detail.value.payments_en = res[1].data.payments_en
    traineeStatus.value.paymentStatus = detail.value.payment_status
    console.log('detail.value.payments_en.lenght', detail.value.payments_en[0])
  }).catch((err) => {
    console.error('getTraineeDetail err', err)
  }).finally(() => {
    rootAppProvide.value.closeContentsLoading()
  })
}

/*
 * PDF関連
 */

const targetPDF = ref('')
const isPdfDownloading = ref(false)

// PDF確認モーダルを開く
const confirmPDFDL = (type) => {
  showConfirmPDFModal.value = true
  targetPDF.value = type
}

// PDF確認モーダルを閉じて初期化
const cancelPDFDL = () => {
  showConfirmPDFModal.value = false
  targetPDF.value = null
}

const PDFModalAgree = () => {
  if (isPdfDownloading.value || !targetPDF.value) return

  showConfirmPDFModal.value = false
  isPdfDownloading.value = true
  rootAppProvide.value.openContentsLoading()

  downloadTraineePdf(targetPDF.value, props.courseId)
    .catch((err) => {
      if (!isSessionExpiredPdfError(err)) {
        console.error('PDF download err', err)
      }
    })
    .finally(() => {
      isPdfDownloading.value = false
      targetPDF.value = null
      rootAppProvide.value.closeContentsLoading()
    })
}

const getStatus = (enabled, attendance) => {
  if (enabled === 'f') {
    return 'キャンセル'
  } else if (attendance === null) {
    return '未受講'
  } else if (attendance === true) {
    return '受講完了'
  } else {
    return '欠席'
  }
}

const isWithinCertificateIssueTerm = computed(() => {
  if (!detail.value) return false
  return commonUtil.isWithinCertificateIssueTerm(detail.value)
})

const openTraineeCard = () => {
  // 今開いているURLに/cardを追加して新しいウィンドウで開く
  window.open(location.href + '/card', '_blank')
}

onMounted(async () => {
  getDetail()
})
</script>

<template>
  <div class="container">
    <h2>受講履歴詳細</h2>
    <div
      v-if="detail"
      class="detail"
    >
      <table class="detailTable">
        <tr>
          <th>講座名称</th>
          <td>{{ detail.course.displays && detail.course.displays.name ? detail.course.displays.name : '' }}</td>
        </tr>
        <tr>
          <th>講座タイプ</th>
          <td>{{ detail.course.type === 'online' ? (detail.course.besent ? 'オンデマンド＋DVD' : 'オンデマンド') : '会場' }}</td>
        </tr>
        <tr v-if="detail.course.type === 'online'">
          <th>DVD送付</th>
          <td>{{ detail.course.besent ? '有' : '無' }}</td>
        </tr>
        <tr v-if="detail.course.type === 'real'">
          <th>会場名</th>
          <td>{{ detail.course.location.displays.name }}</td>
        </tr>
        <tr v-if="detail.course.type === 'real'">
          <th>会場住所</th>
          <td>〒{{ detail.course.location.displays.address.zipcode }} {{ detail.course.location.displays.address.pref }} {{ detail.course.location.displays.address.city }} {{ detail.course.location.displays.address.street }} {{ detail.course.location.displays.address.building }}</td>
        </tr>
        <tr v-if="detail.course.type === 'real'">
          <th>会場地図</th>
          <td>{{ detail.course.location.latlong }}</td>
        </tr>
        <tr>
          <th>開催期間</th>
          <td>
            {{ dayjs(detail.course.term[0]).format('YYYY/MM/DD(dd) HH:mm') }}～
            {{ dayjs(detail.course.term[1]).format('YYYY/MM/DD(dd) HH:mm') }}
          </td>
        </tr>
        <tr v-if="detail.course.type === 'real'">
          <th>定員</th>
          <td>
            {{ detail.course.displays.capacity }}
          </td>
        </tr>
        <tr>
          <th>受講料</th>
          <td>
            {{ detail.course.price.toLocaleString() }}円（税込）
          </td>
        </tr>
        <tr>
          <th>支払い状況</th>
          <td>
            {{ detail.payment_status === -1 ? '未払い' : detail.payment_status === -2 ? '返金済' : '支払済' }}
            <template v-if="detail.payment_status === 1">
              （{{ detail.payment_by === 'GMOPay/KONBINI' ? 'コンビニ払い' : detail.payment_by === 'GMOPay/CREDIT' ? 'クレジットカード' : '銀行振込' }}）
            </template>
            <button
              v-if="detail.bill_issue"
              class="c-button c-button--small c-button--yellow"
              @click="confirmPDFDL('bill')"
            >
              請求書の発行
            </button>
            <button
              v-if="detail.receipt_issue"
              class="c-button c-button--small c-button--yellow"
              @click="confirmPDFDL('receipt')"
            >
              領収書の発行
            </button>
          </td>
        </tr>
        <tr v-if="detail.payment_status === 1">
          <th>受講状況</th>
          <td>
            {{ commonUtil.getTraineeStatus(detail).name }}
          </td>
        </tr>
        <tr v-else>
          <th>受講状態</th>
          <td>
            {{ getStatus(detail.enabled, detail.attendance) }}
          </td>
        </tr>
        <tr v-if="detail.certificate">
          <th>修了証No</th>
          <td>
            {{ detail.certificate }}
          </td>
        </tr>
      </table>

      <!-- フッター -->
      <div class="btnContainer">
        <button
          type="submit"
          class="c-button c-button--medium c-button--border"
          @click="router.push({name: 'History'})"
        >
          一覧に戻る
        </button>

        <button
          v-if="detail.payment_status === 1 && detail.course.type === 'real'"
          class="c-button c-button--medium c-button--yellow"
          @click="openTraineeCard"
        >
          受講票を表示
        </button>

        <button
          v-if="detail.certificate_issue"
          class="c-button c-button--medium c-button--yellow"
          :disabled="!isWithinCertificateIssueTerm"
          @click="confirmPDFDL('cert')"
        >
          <span v-if="isWithinCertificateIssueTerm">
            修了証の発行
          </span>
          <span v-else>
            修了証の発行期間外
          </span>
        </button>
      </div>
    </div>
    <div
      v-else
      class="detail"
    >
      <p>講座の情報がありません。</p>
    </div>

    <Modal :show="showConfirmPDFModal">
      <template #body>
        <div class="l-modal__body">
          <h2 class="l-modal__ttl">
            一度発行した場合内容の変更はできません。<br>
            登録内容を確認してください。<br>
            ※以前ダウンロードしている場合は<br>
            同様の内容がダウンロードされます。
          </h2>
          <div class="l-modal__buttons l-modal__buttons--twin">
            <button
              class="c-modalBtn c-modalBtn--twin c-modalBtn--black"
              type="button"
              @click="cancelPDFDL"
            >
              いいえ
            </button>
            <button
              class="c-modalBtn c-modalBtn--twin c-modalBtn--gray"
              type="button"
              :disabled="isPdfDownloading"
              @click="PDFModalAgree"
            >
              はい
            </button>
          </div>
        </div>
      </template>
    </Modal>
  </div>
</template>

<style scoped lang="scss">
.container {
  h2 {
    padding: 30px 0 15px;
    font-size: 26px;
    text-align: left;
    font-weight: bold;
  }

  .detail {
    width: 100%;

    table.detailTable {
      width: 100%;
      border-top: 1px solid #ddd;
      
      th {
        width: 200px;
        height: 100%;
        padding: 8px 15px 10px;
        background: #eaeaea;
        border-bottom: 1px solid #ddd;
        font-size: 16px;
        text-align: right;
      }

      td {
        width: calc(100% - 200px);
        padding: 8px 15px 10px;
        border-bottom: 1px solid #ddd;
        font-size: 16px;

        button {
          display: inline-block;
          width: auto;
          padding: 3px 8px;
          vertical-align: 1px;
          margin: 0 10px 0 0;
        }
      }
    }
  }

  /* フッターボタン */
  .btnContainer {
    display: flex;
    width: auto;
    margin: 20px auto;
    justify-content: center;

    button {
      width: 200px;

      &:nth-child(2) {
        margin-left: 20px;
      }
    }
  }
}

@media (max-width: 576px) {
  .container .detail table.detailTable {
    th {
      width: auto;
    }
  }
}

.attention {
  color: red;
  font-size: 14px;
}
</style>
